import { Card } from "@/components/ui/card";
import { ParcelOperatorLocation } from "@/api/use-parcel-live-tracking";
import { Lock, Truck, MapPin, Navigation } from "lucide-react";
import { useMemo, useEffect } from "react";
import {
  APIProvider,
  Map,
  AdvancedMarker,
  useMap,
} from "@vis.gl/react-google-maps";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface OperatorLocationProps {
  data?: ParcelOperatorLocation | { requiresAuth: boolean; error: string };
  isLoading: boolean;
  error?: Error;
  onAuthRequest?: () => void;
}

/**
 * Component to automatically fit map bounds to include all markers
 * Uses the useMap hook to access the map instance and adjust its bounds
 */
function BoundsController({
  locations,
}: {
  locations: google.maps.LatLngLiteral[];
}) {
  const map = useMap();

  useEffect(() => {
    if (!map || !locations.length) return;

    try {
      const bounds = new google.maps.LatLngBounds();
      locations.forEach((location) => bounds.extend(location));

      // Fit the map to the bounds with some padding
      map.fitBounds(bounds);
    } catch (error) {
      console.error("Error fitting bounds:", error);
    }
  }, [map, locations]);

  return null;
}

/**
 * Operator Location component displays a map with the current location of the delivery vehicle,
 * destination, and any waypoints in between. It also shows estimated arrival time.
 */
export function OperatorLocation({
  data,
  isLoading,
  error,
  onAuthRequest,
}: OperatorLocationProps) {
  // Check if authentication is required
  const requiresAuth = !!(data && "requiresAuth" in data && data.requiresAuth);

  // Only proceed with location data if we have it and authentication is not required
  const locationData =
    !requiresAuth && data && !("requiresAuth" in data) ? data : undefined;

  // Default map center (fallback to Toronto if no location data)
  const mapCenter = useMemo(
    () => ({
      lat: locationData?.location.lat ?? 43.6532,
      lng: locationData?.location.lng ?? -79.3832,
    }),
    [locationData?.location]
  );

  // Collect all locations for bounds calculation
  const allLocations = useMemo(() => {
    if (!locationData) return [];

    return [
      locationData.location,
      locationData.path.destination,
      ...locationData.path.waypoints,
    ];
  }, [locationData]);

  // Calculate appropriate zoom level based on stops away
  const zoomLevel = useMemo(() => {
    if (!locationData) return 12;
    return locationData.stopsAway > 5
      ? 10
      : locationData.stopsAway > 2
        ? 11
        : 12;
  }, [locationData]);

  // Get status configuration based on stops away
  const getStatusConfig = useMemo(() => {
    if (!locationData)
      return {
        bgColor: "bg-blue-50",
        textColor: "text-blue-700",
        icon: Navigation,
      };

    if (locationData.stopsAway === 0) {
      return {
        bgColor: "bg-green-50",
        textColor: "text-green-700",
        icon: Navigation,
      };
    } else if (locationData.stopsAway <= 2) {
      return {
        bgColor: "bg-blue-50",
        textColor: "text-blue-700",
        icon: Truck,
      };
    } else {
      return {
        bgColor: "bg-amber-50",
        textColor: "text-amber-700",
        icon: Truck,
      };
    }
  }, [locationData]);

  // Loading state
  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="h-[200px] animate-pulse bg-muted rounded-md" />
      </Card>
    );
  }

  // Authentication required state
  if (requiresAuth) {
    return (
      <Card className="overflow-hidden">
        <div className="border-b bg-muted/5 px-6 py-4">
          <h3 className="font-medium">Live Tracking</h3>
        </div>
        <div className="p-6">
          <div className="flex flex-col items-center justify-center space-y-4 py-8 text-center">
            <Lock className="h-10 w-10 text-muted-foreground" />
            <h3 className="font-medium">Authentication Required</h3>
            <p className="text-sm text-muted-foreground max-w-md">
              Please verify your identity to access live tracking information.
            </p>
            {onAuthRequest && (
              <Button
                onClick={onAuthRequest}
                className="mt-2"
                variant="outline"
              >
                Verify Identity
              </Button>
            )}
          </div>
        </div>
      </Card>
    );
  }

  // Error or no data state
  if (error || !locationData) {
    return null;
  }

  // Format time to show only hours and minutes
  const formatArrivalTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const StatusIcon = getStatusConfig.icon;

  // Main component render
  return (
    <Card className="overflow-hidden">
      <div className="border-b bg-muted/5 px-6 py-4 flex justify-between items-center">
        <h3 className="font-medium">Live Tracking</h3>
        <div
          className={cn(
            "rounded-full px-3 sm:px-4 py-1.5 sm:py-2",
            getStatusConfig.bgColor,
            getStatusConfig.textColor
          )}
        >
          <div className="flex items-center gap-1.5 sm:gap-2">
            <StatusIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span className="text-xs sm:text-sm font-medium">
              {locationData.stopsAway === 0
                ? "Delivery Vehicle Approaching"
                : `${locationData.stopsAway} ${locationData.stopsAway === 1 ? "stop" : "stops"} away`}
            </span>
          </div>
        </div>
      </div>
      <div className="p-6">
        <div className="space-y-6">
          {/* Map */}
          <div className="h-[250px] rounded-md overflow-hidden">
            <APIProvider
              apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
              libraries={["marker"]}
            >
              <Map
                defaultZoom={zoomLevel}
                defaultCenter={mapCenter}
                gestureHandling="greedy"
                disableDefaultUI
                mapId="3667d2e0a1e93d2b"
                style={{ width: "100%", height: "100%" }}
              >
                {/* Component to fit bounds */}
                <BoundsController locations={allLocations} />

                {/* Current location marker (operator) */}
                <AdvancedMarker
                  position={locationData.location}
                  title="Delivery Vehicle"
                >
                  <div className="bg-blue-600 text-white px-2 py-1 rounded text-sm flex items-center gap-1">
                    <Truck className="h-4 w-4" />
                    <span>Delivery Vehicle</span>
                  </div>
                </AdvancedMarker>

                {/* Destination marker */}
                <AdvancedMarker
                  position={locationData.path.destination}
                  title="Destination"
                >
                  <div className="bg-red-600 text-white px-2 py-1 rounded text-sm flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    <span>Destination</span>
                  </div>
                </AdvancedMarker>

                {/* Waypoint markers */}
                {locationData.path.waypoints.map((waypoint, index) => (
                  <AdvancedMarker
                    key={`waypoint-${index}`}
                    position={waypoint}
                    title={`Stop ${index + 1}`}
                  >
                    <div className="bg-yellow-500 text-white px-2 py-1 rounded text-sm flex items-center gap-1">
                      <span className="font-bold">{index + 1}</span>
                      <span>Stop</span>
                    </div>
                  </AdvancedMarker>
                ))}
              </Map>
            </APIProvider>
          </div>

          {/* Estimated Arrival with Progress Bar */}
          <div className="space-y-2 animate-pulse">
            <div className="text-md font-semibold">
              Estimated arrival:{" "}
              {formatArrivalTime(locationData.estimatedArrival)}
            </div>
            <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
              <div
                className={cn("h-full bg-black", " bg-opacity-70")}
                style={{ width: "66.66%" }}
              />
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
